import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { buildDeck } from '../../domain/game/overtakeDeck';
import { startMatch } from '../../domain/game/overtakeMatch';
import { applyMove } from '../../domain/game/overtakeMoves';
import { chooseMove } from '../../domain/game/overtakeOpponent';

/**
 * A match of the Overtake, bound to the cars on screen (SPEC-011 PR-11.3).
 *
 * Everything about WHAT happens is `domain/game`; this hook is the three things
 * that cannot be pure — when the deck is rebuilt, which draw of `Math.random`
 * the deal gets, and the pause that makes the opponent feel like somebody
 * thinking rather than a function returning.
 *
 * The shape is `useDuelRun`'s, deliberately: the domain takes `random` as a
 * defaulted parameter resolved at CALL time and this hook never passes one, so a
 * suite pinning `Math.random` pins the deal without the production path carrying
 * a seam it does not use.
 *
 * A match is NOT started on mount. The screen offers a setup first — who to play
 * and who names the figure — because both are real choices and neither has a
 * default that is right for everybody. `match` is null until the reader deals,
 * and null again when they walk away from one.
 */

/**
 * How long the opponent appears to think.
 *
 * Long enough that a card does not simply appear on the table — a move the
 * reader did not see happen is a move they have to reconstruct from the result —
 * and short enough that it never feels like waiting for a network. A single
 * constant rather than a range, because a variable delay at this length reads as
 * inconsistent rather than as lifelike.
 */
export const OPPONENT_THINKING_MS = 850;

/** The statuses in which somebody actually has a move to make. */
const AWAITING_A_MOVE = ['leading', 'renaming', 'answering'];

export default function useOvertakeMatch({ cars }) {
    const deck = useMemo(() => buildDeck(cars), [cars]);
    // The ids in the CATALOGUE's own order. The shuffle is the deal's business,
    // and sorting here would only hide a caller that handed them over unsorted.
    const ids = useMemo(() => [...deck.keys()], [deck]);
    const [match, setMatch] = useState(null);
    /**
     * The settings this match was dealt under.
     *
     * Held here rather than on the screen because they outlive one match: "play
     * again" has to deal the SAME game rather than reopening the setup, and the
     * opponent's skill has to survive every move without being re-passed. They
     * are fixed at the deal and never edited — a reader who could change the
     * lead rule mid-match would change who names the next figure, which is the
     * one rule the whole match has been played against.
     */
    const [options, setOptions] = useState(null);

    const deal = useCallback((chosen) => {
        setOptions(chosen);
        setMatch(startMatch(ids, chosen));
    }, [ids]);

    /** The same game again, without asking the two questions a second time. */
    const redeal = useCallback(
        () => setMatch(startMatch(ids, options ?? {})),
        [ids, options],
    );

    /** Back to the setup, rather than straight into another deal. */
    const leave = useCallback(() => setMatch(null), []);

    const move = useCallback(
        (played) => setMatch((current) => applyMove(current, deck, played)),
        [deck],
    );

    /**
     * The opponent's turn.
     *
     * One timer, cleared on every change, and it re-checks whose turn it is
     * INSIDE the updater before it moves. Both halves matter: without the clear,
     * a reader who passes while the opponent is thinking gets two moves out of
     * one; without the re-check, a timer that fires in the same frame as a tap
     * plays a move against a match that has already moved on.
     *
     * Guarded on the STATUS as well as the turn, because `pass` leaves the turn
     * on whoever gave up. Without that guard the opponent would be handed a
     * round-over board it has no move for, on every round it lost.
     */
    const skill = options?.skill;

    useEffect(() => {
        if (match?.turn !== 'opponent' || !AWAITING_A_MOVE.includes(match.status)) {
            return undefined;
        }

        const thinking = setTimeout(() => {
            setMatch((current) => (current?.turn === 'opponent'
                ? applyMove(current, deck, chooseMove(current, deck, { skill }))
                : current));
        }, OPPONENT_THINKING_MS);

        return () => clearTimeout(thinking);
    }, [match, deck, skill]);

    return {
        // Null before the reader has dealt, which is the screen's setup state.
        match,
        deck,
        deal,
        redeal,
        leave,
        // Every move goes through one door, so the screen holds no rulebook of
        // its own and a move that is refused simply leaves the state alone.
        move,
        // True while the reader is watching rather than acting. The hand is
        // disabled on it, so a tap during the pause cannot queue a move against
        // a board that is about to change.
        isWaiting: match?.turn === 'opponent' && AWAITING_A_MOVE.includes(match.status),
    };
}
